/**
 * SmartChat - Sage AI Chat Interface
 *
 * A floating chat panel that connects users with Sage, their AI advocate,
 * advisor, and guide through the divorce process.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquare, X, Send, Square, Loader2, RefreshCw, Trash2, ChevronDown, Sparkles, Paperclip, Image as ImageIcon, FileText, Copy, Check, Download, Pencil, Pin, FileDown, Code, Bookmark, FolderOpen } from 'lucide-react';
import { trackEvent } from '@/lib/posthog';
import { downloadDocumentAsPdf, copyAsHtml, downloadAsTextFile } from '@/lib/pdf/generateDocumentPdf';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useSageChat, type SageMessage, type ToolCall, type ImageAttachment, type ImageMediaType } from '@/hooks/useSageChat';
import { DocumentPicker, type DocumentReference } from './DocumentPicker';
import { CitationCard } from './CitationCard';
import { UsageGateModal } from '@/components/UsageGateModal';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { toast } from 'sonner';
import { processFileForSage, type ProcessedFile } from '@/lib/sage/processFile';
import { SAGE_ACCEPT, SAGE_MAX_FILE_SIZE, SAGE_MAX_ATTACHMENTS } from '@/lib/sage/fileTypes';

// Pending attachment for UI display
interface PendingImageAttachment {
  id: string;
  type: 'image';
  file?: File;
  previewUrl: string;
  attachment: ImageAttachment;
  isPdfPage?: boolean;
  pdfPageNumber?: number;
  pdfFilename?: string;
}

/** Holds OCR text for PDF pages that exceed the image threshold */
interface PendingPdfTextAttachment {
  id: string;
  type: 'pdf_text';
  pdfFilename: string;
  ocrText: string;
  totalPages: number;
  strategy: 'hybrid' | 'text_only';
}

/** Holds extracted text from Word, spreadsheet, or plain text files */
interface PendingDocTextAttachment {
  id: string;
  type: 'doc_text';
  filename: string;
  text: string;
  format: string;
  wordCount: number;
  truncated: boolean;
}

/** Holds a reference to an existing case correspondence document or email thread */
interface PendingDocumentAttachment {
  id: string;
  type: 'document_reference';
  documentId: string;
  documentType: 'correspondence' | 'email_thread';
  title: string;
  date?: string;
  summary?: string;
}

type PendingAttachment = PendingImageAttachment | PendingPdfTextAttachment | PendingDocTextAttachment | PendingDocumentAttachment;

// ============================================================================
// Suggested Questions
// ============================================================================

const SUGGESTED_QUESTIONS = [
  "What's my current net worth and how would different splits affect me?",
  "What patterns do you see in my spouse's correspondence?",
  "What are my strongest negotiation leverage points?",
  "Walk me through the best and worst case scenarios",
  "What deadlines should I prioritize this week?",
  "I'm feeling overwhelmed - what should I focus on right now?",
];

// ============================================================================
// Main Component
// ============================================================================

interface SmartChatProps {
  hidden?: boolean;
  /** When true, removes fixed positioning for use inside a dock container */
  docked?: boolean;
}

export function SmartChat({ hidden = false, docked = false }: SmartChatProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  const [showUsageGate, setShowUsageGate] = useState(false);
  const [pinnedMessageIds, setPinnedMessageIds] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const { subscription } = useSubscription();
  const { user } = useAuth();

  const {
    messages,
    isStreaming,
    isThinking,
    currentToolCall,
    error,
    sendMessage,
    stopMessage,
    retry,
    clearHistory,
    clearError,
  } = useSageChat({
    onError: (err) => console.error('Sage error:', err),
    onUsageLimitHit: () => {
      setShowUsageGate(true);
    },
  });

  // Workspace handlers — MUST be after useSageChat to avoid TDZ
  const handleSaveToCase = useCallback(async (content: string, label: string) => {
    if (!user?.id) return;
    const { error: insertError } = await supabase
      .from('session_notes')
      .insert({
        user_id: user.id,
        title: label,
        content,
        note_type: 'sage_document',
        event_date: new Date().toISOString(),
      });
    if (insertError) {
      toast.error('Failed to save to case notes');
      return;
    }
    toast.success('Saved to case notes!');
    trackEvent('sage_document_saved_to_case', { label });
  }, [user?.id]);

  const handlePinMessage = useCallback((messageId: string) => {
    setPinnedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  }, []);

  const handleEditMessage = useCallback(async (_messageId: string, newContent: string) => {
    await sendMessage(newContent);
  }, [sendMessage]);

  const handleExportConversation = useCallback(() => {
    if (messages.length === 0) return;
    const lines = messages.map((m) => {
      const role = m.role === 'user' ? 'You' : 'Sage';
      const time = m.timestamp.toLocaleString();
      return `[${time}] ${role}:\n${m.content}\n`;
    });
    const text = `Sage Conversation Export\n${'='.repeat(40)}\n\n${lines.join('\n')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sage-conversation-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    trackEvent('sage_conversation_exported', { messageCount: messages.length });
  }, [messages]);

  const handleDocumentSelect = useCallback((doc: DocumentReference) => {
    if (pendingAttachments.length >= SAGE_MAX_ATTACHMENTS) return;
    if (pendingAttachments.some((a) => a.type === 'document_reference' && (a as PendingDocumentAttachment).documentId === doc.id)) return;

    setPendingAttachments((prev) => [
      ...prev,
      {
        id: `doc-${doc.id}`,
        type: 'document_reference',
        documentId: doc.id,
        documentType: doc.type === 'email_thread' ? 'email_thread' : 'correspondence',
        title: doc.title,
        date: doc.date,
        summary: doc.summary,
      },
    ]);
    trackEvent('sage_document_attachment_added', { documentId: doc.id, source: 'smart_chat' });
  }, [pendingAttachments]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      // Use requestAnimationFrame for smoother mobile scrolling
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [messages, isThinking]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle file selection — unified processor for all file types
  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newAttachments: PendingAttachment[] = [];

    for (const file of fileArray) {
      if (pendingAttachments.length + newAttachments.length >= SAGE_MAX_ATTACHMENTS) {
        toast.warning('Maximum attachments reached');
        break;
      }

      if (file.size > SAGE_MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large (max 20 MB)`);
        continue;
      }

      // Show processing indicator for PDFs
      const wasPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (wasPdf) setIsProcessingPdf(true);

      try {
        const results = await processFileForSage(file);

        for (const result of results) {
          if (pendingAttachments.length + newAttachments.length >= SAGE_MAX_ATTACHMENTS) break;

          switch (result.kind) {
            case 'image':
              newAttachments.push({
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                type: 'image',
                file: result.file,
                previewUrl: result.previewUrl,
                attachment: result.attachment,
              });
              break;

            case 'pdf_page':
              newAttachments.push({
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}-p${result.pageNumber}`,
                type: 'image',
                previewUrl: result.previewUrl,
                attachment: result.attachment,
                isPdfPage: true,
                pdfPageNumber: result.pageNumber,
                pdfFilename: result.pdfFilename,
              });
              break;

            case 'pdf_text':
              newAttachments.push({
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}-ocr`,
                type: 'pdf_text',
                pdfFilename: result.pdfFilename,
                ocrText: result.ocrText,
                totalPages: result.totalPages,
                strategy: result.strategy,
              });
              break;

            case 'document_text':
              newAttachments.push({
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}-doc`,
                type: 'doc_text',
                filename: result.filename,
                text: result.text,
                format: result.format,
                wordCount: result.wordCount,
                truncated: result.truncated,
              });
              break;

            case 'error':
              toast.error(`Failed to process ${result.filename}: ${result.reason}`);
              break;
          }
        }
      } catch (err) {
        console.error('Failed to process file:', file.name, err);
        toast.error(`Failed to process ${file.name}`);
      } finally {
        if (wasPdf) setIsProcessingPdf(false);
      }
    }

    if (newAttachments.length > 0) {
      setPendingAttachments((prev) => [...prev, ...newAttachments]);
    }
  }, [pendingAttachments.length]);

  // Remove attachment
  const removeAttachment = useCallback((id: string) => {
    setPendingAttachments((prev) => {
      const attachment = prev.find((a) => a.id === id);
      if (attachment && attachment.type === 'image' && attachment.file && !attachment.isPdfPage) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  // Handle paste
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault();
      handleFiles(imageFiles);
    }
  }, [handleFiles]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  const handleSend = async () => {
    if ((!inputValue.trim() && pendingAttachments.length === 0) || isStreaming) return;

    let message = inputValue || (pendingAttachments.length > 0 ? 'What can you tell me about this?' : '');

    // Extract image attachments (skip text-based - those go into the message)
    const imageAttachments = pendingAttachments.filter((p): p is PendingImageAttachment => p.type === 'image');
    const documentAttachments = pendingAttachments.filter((p): p is PendingDocumentAttachment => p.type === 'document_reference');
    const attachments = [
      ...imageAttachments.map((p) => p.attachment),
      ...documentAttachments.map((d) => ({
        type: 'document_reference' as const,
        documentId: d.documentId,
        documentType: d.documentType,
      })),
    ];

    // Append OCR text from PDF text attachments to the message
    const pdfTextAttachments = pendingAttachments.filter((p): p is PendingPdfTextAttachment => p.type === 'pdf_text');
    for (const pdfText of pdfTextAttachments) {
      const label = pdfText.strategy === 'text_only'
        ? `[Full OCR text from "${pdfText.pdfFilename}" (${pdfText.totalPages} pages):]`
        : `[OCR text for remaining pages of "${pdfText.pdfFilename}" (pages 21-${pdfText.totalPages}, images sent for pages 1-20):]`;
      message += `\n\n${label}\n${pdfText.ocrText}`;
    }

    // Append extracted text from Word, spreadsheet, and text files
    const docTextAttachments = pendingAttachments.filter((p): p is PendingDocTextAttachment => p.type === 'doc_text');
    for (const doc of docTextAttachments) {
      const formatLabel = { docx: 'Word document', doc: 'Word document', xlsx: 'spreadsheet', xls: 'spreadsheet', csv: 'CSV', txt: 'text file', rtf: 'RTF document' }[doc.format] || 'document';
      message += `\n\n[Contents of "${doc.filename}" (${formatLabel}, ${doc.wordCount.toLocaleString()} ${doc.format === 'csv' || doc.format === 'xlsx' || doc.format === 'xls' ? 'rows' : 'words'})${doc.truncated ? ' — truncated' : ''}:]\n${doc.text}`;
    }

    // Clear input and attachments
    setInputValue('');
    pendingAttachments.forEach((p) => {
      if (p.type === 'image' && p.previewUrl && !p.isPdfPage) {
        URL.revokeObjectURL(p.previewUrl);
      }
    });
    setPendingAttachments([]);

    await sendMessage(message, attachments);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    setInputValue(question);
    inputRef.current?.focus();
  };

  // Don't render if hidden
  if (hidden) return null;

  return (
    <>
    {/* Usage gate modal for sage messages */}
    <UsageGateModal
      feature="sage"
      used={subscription?.sage_messages_sent ?? 0}
      limit={subscription?.sage_messages_limit ?? 0}
      open={showUsageGate}
      onOpenChange={setShowUsageGate}
    />
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      {/* Floating Button - Premium Sage styling */}
      <SheetTrigger asChild>
        <Button
          size="lg"
          className={cn(
            'h-12 w-12 sm:h-14 sm:w-14 rounded-full',
            !docked && 'fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50',
            'bg-gradient-to-br from-navy-600 via-navy-700 to-navy-800',
            'hover:from-navy-500 hover:via-navy-600 hover:to-navy-700',
            'shadow-lg shadow-navy-900/30 hover:shadow-xl hover:shadow-navy-900/40',
            'border border-navy-500/20',
            'transition-all duration-300 hover:scale-110',
            'ring-2 ring-amber-400/50 hover:ring-amber-400',
            isStreaming && 'animate-pulse ring-amber-400'
          )}
        >
          <Sparkles className="h-6 w-6 text-amber-300" />
          <span className="sr-only">Open Sage Chat</span>
        </Button>
      </SheetTrigger>

      {/* Chat Panel */}
      <SheetContent
        side="right"
        className="w-[100vw] sm:w-[440px] md:w-[500px] max-w-full p-0 flex flex-col h-[100dvh] sm:h-full"
      >
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b bg-gradient-to-r from-navy-600 to-navy-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-white" />
              </div>
              <div>
                <SheetTitle className="text-white text-lg font-display">Sage</SheetTitle>
                <p className="text-white/70 text-xs">Your Advocate & Strategy Advisor</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 sm:h-8 sm:w-8 text-white/70 hover:text-white hover:bg-white/10"
                  onClick={handleExportConversation}
                  title="Export conversation"
                >
                  <FileDown className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 sm:h-8 sm:w-8 text-white/70 hover:text-white hover:bg-white/10"
                onClick={clearHistory}
                title="Clear history"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 sm:h-8 sm:w-8 text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* Messages Area */}
        <ScrollArea ref={scrollRef} className="flex-1 min-h-0 p-4 overscroll-contain">
          <div className="space-y-4">
            {/* Empty State */}
            {messages.length === 0 && !isThinking && (
              <div className="text-center py-8">
                <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-navy-500 to-navy-700 flex items-center justify-center shadow-lg">
                  <Sparkles className="h-8 w-8 text-amber-300" />
                </div>
                <h3 className="font-display text-lg text-navy-800 mb-2">
                  Hey, I'm Sage
                </h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
                  I'm here to help you navigate your divorce. I have access to all your case data
                  and I'm completely on your side.
                </p>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground mb-2">Try asking:</p>
                  {SUGGESTED_QUESTIONS.slice(0, 3).map((q, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      size="sm"
                      className="w-full text-left justify-start h-auto min-h-[44px] py-2.5 px-3 text-xs whitespace-normal"
                      onClick={() => handleSuggestedQuestion(q)}
                    >
                      {q}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                onEdit={handleEditMessage}
                onPin={handlePinMessage}
                isPinned={pinnedMessageIds.has(message.id)}
                onSaveDocument={handleSaveToCase}
              />
            ))}

            {/* Thinking Indicator */}
            {isThinking && (
              <div className="flex items-center gap-3 text-muted-foreground">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-navy-500 to-navy-700 flex items-center justify-center shadow-md ring-2 ring-white">
                  <Loader2 className="h-4 w-4 text-amber-300 animate-spin" />
                </div>
                <span className="text-sm">
                  {currentToolCall
                    ? `Analyzing ${formatToolName(currentToolCall.name)}...`
                    : 'Thinking...'}
                </span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800 mb-2">{error}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={retry} className="min-h-[44px]">
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Retry
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearError} className="min-h-[44px]">
                    Dismiss
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div
          ref={dropZoneRef}
          className={cn(
            'border-t px-3 py-3 sm:p-4 bg-white transition-colors pb-[max(0.75rem,env(safe-area-inset-bottom))]',
            isDragging && 'bg-navy-50 border-navy-300'
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drag overlay */}
          {isDragging && (
            <div className="mb-3 p-3 border-2 border-dashed border-navy-300 rounded-lg bg-navy-50/50 text-center">
              <ImageIcon className="h-6 w-6 mx-auto mb-1 text-navy-500" />
              <p className="text-xs text-navy-600 font-medium">Drop images or PDFs here</p>
            </div>
          )}

          {/* PDF processing indicator */}
          {isProcessingPdf && (
            <div className="mb-3 p-2 bg-navy-50 rounded-lg flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin text-navy-600" />
              <span className="text-xs text-navy-600">Processing PDF...</span>
            </div>
          )}

          {/* Attachment previews */}
          {pendingAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {pendingAttachments.map((att) => (
                <div key={att.id} className="relative group">
                  {att.type === 'image' ? (
                    <>
                      <img
                        src={att.previewUrl}
                        alt={att.attachment.filename || 'Attached image'}
                        className="h-12 w-12 object-cover rounded-lg border"
                      />
                      {att.isPdfPage && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center py-0.5 rounded-b-lg">
                          <FileText className="h-2 w-2 inline mr-0.5" />
                          p{att.pdfPageNumber}
                        </div>
                      )}
                    </>
                  ) : att.type === 'document_reference' ? (
                    <div className="h-12 px-2 flex items-center gap-1.5 rounded-lg border bg-navy-50 border-navy-200 text-[10px] text-navy-700 max-w-[130px]">
                      <FolderOpen className="h-3 w-3 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{att.title}</p>
                        {att.date && <p className="text-navy-400">{att.date}</p>}
                      </div>
                    </div>
                  ) : att.type === 'doc_text' ? (
                    <div className="h-12 px-2 flex items-center gap-1.5 rounded-lg border bg-amber-50 border-amber-200 text-[10px] text-amber-700 max-w-[120px]">
                      <FileText className="h-3 w-3 shrink-0" />
                      <span className="truncate">{att.filename}</span>
                    </div>
                  ) : (
                    <div className="h-12 px-2 flex items-center gap-1 rounded-lg border bg-slate-50 text-[10px] text-slate-600">
                      <FileText className="h-3 w-3" />
                      <span>{att.totalPages}p OCR</span>
                    </div>
                  )}
                  <button
                    onClick={() => removeAttachment(att.id)}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-1.5 sm:gap-2">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept={SAGE_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) handleFiles(e.target.files);
                e.target.value = '';
              }}
            />

            {/* Attach button */}
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-11 w-11 shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={isStreaming || isProcessingPdf || pendingAttachments.length >= 5}
              title="Attach images or PDFs"
            >
              {isProcessingPdf ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Paperclip className="h-4 w-4" />
              )}
            </Button>

            {/* Document reference picker */}
            <DocumentPicker
              onSelect={handleDocumentSelect}
              disabled={isStreaming || pendingAttachments.length >= SAGE_MAX_ATTACHMENTS}
              selectedIds={pendingAttachments
                .filter((a): a is PendingDocumentAttachment => a.type === 'document_reference')
                .map((a) => a.documentId)}
              compact
            />

            <Textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={pendingAttachments.length > 0 ? "Add a message..." : "Ask about your case..."}
              className="min-h-[44px] max-h-32 resize-none text-sm sm:text-base text-foreground"
              disabled={isStreaming}
            />
            {isStreaming ? (
              <Button
                onClick={stopMessage}
                variant="destructive"
                className="h-11 w-11 shrink-0"
                title="Stop response"
              >
                <Square className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSend}
                disabled={!inputValue.trim() && pendingAttachments.length === 0}
                className="h-11 w-11 shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
          {/* Sage message limit indicator */}
          {subscription && subscription.sage_messages_limit != null && subscription.sage_messages_limit > 0 && (() => {
            const sent = subscription.sage_messages_sent ?? 0;
            const limit = subscription.sage_messages_limit;
            const remaining = limit - sent;
            const pct = (sent / limit) * 100;

            if (remaining <= 0) {
              return (
                <div className="flex items-center justify-center gap-2 mt-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <span className="text-xs font-medium text-red-700 dark:text-red-300">
                    No messages remaining.
                  </span>
                  <button
                    onClick={() => setShowUsageGate(true)}
                    className="text-xs font-semibold text-red-700 dark:text-red-300 underline hover:text-red-900 min-h-[44px] py-2"
                  >
                    Upgrade for more
                  </button>
                </div>
              );
            }
            if (remaining === 1) {
              return (
                <div className="flex items-center justify-center gap-2 mt-2 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <span className="text-xs font-medium text-red-600 dark:text-red-400">
                    Last free message.
                  </span>
                  <button
                    onClick={() => setShowUsageGate(true)}
                    className="text-xs font-semibold text-red-600 dark:text-red-400 underline hover:text-red-800 min-h-[44px] py-2"
                  >
                    Upgrade for {limit >= 100 ? 'unlimited' : `${limit * 10} messages`}
                  </button>
                </div>
              );
            }
            if (remaining <= 3 && pct >= 60) {
              return (
                <div className="flex items-center justify-center gap-2 mt-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                    {remaining} messages remaining.
                  </span>
                  <button
                    onClick={() => setShowUsageGate(true)}
                    className="text-xs font-semibold text-amber-700 dark:text-amber-300 underline hover:text-amber-900 min-h-[44px] py-2"
                  >
                    Upgrade
                  </button>
                </div>
              );
            }
            return null;
          })()}
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Sage provides strategic guidance, not legal advice.
            {pendingAttachments.length === 0 && !isProcessingPdf && <span className="ml-1">• Paste or drag images/PDFs</span>}
          </p>
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
}

// ============================================================================
// Chat Message Component
// ============================================================================

interface ChatMessageProps {
  message: SageMessage;
  onEdit?: (messageId: string, content: string) => void;
  onPin?: (messageId: string) => void;
  isPinned?: boolean;
  onSaveDocument?: (content: string, label: string) => Promise<void>;
}

function ChatMessage({ message, onEdit, onPin, isPinned, onSaveDocument }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const [showTools, setShowTools] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);

  const handleCopy = async () => {
    if (!message.content) return;
    try {
      await navigator.clipboard.writeText(message.content);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = message.content;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(true);
    trackEvent('sage_message_copied', { role: message.role });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEditSubmit = () => {
    if (editContent.trim() && onEdit) {
      onEdit(message.id, editContent.trim());
      trackEvent('sage_message_edited');
    }
    setIsEditing(false);
  };

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      {!isUser && (
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-navy-500 to-navy-700 flex items-center justify-center shrink-0 shadow-md ring-2 ring-white">
          <Sparkles className="h-4 w-4 text-amber-300" />
        </div>
      )}

      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-3 break-words overflow-hidden',
          isUser
            ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-md shadow-amber-500/20'
            : 'bg-white border border-slate-200 shadow-sm'
        )}
      >
        {/* Attachments */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {message.attachments.map((att) => (
              <div key={att.id} className="relative">
                {att.type === 'image' && att.url && (
                  <img
                    src={att.url}
                    alt={att.filename || 'Attached image'}
                    className="max-w-[120px] max-h-[90px] rounded-lg object-cover ring-1 ring-white/20"
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Message Content — with inline edit for user messages */}
        {isUser && isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full bg-white/20 text-white rounded-lg p-2 text-sm resize-none border border-white/30 focus:outline-none focus:border-white/60"
              rows={3}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleEditSubmit}
                className="px-3 py-1 bg-white/20 rounded text-xs font-medium hover:bg-white/30"
              >
                Save & Resend
              </button>
              <button
                onClick={() => { setIsEditing(false); setEditContent(message.content); }}
                className="px-3 py-1 rounded text-xs hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'prose prose-sm max-w-none [overflow-wrap:anywhere]',
              isUser && 'prose-invert'
            )}
          >
            <MessageContent content={message.content} isStreaming={message.isStreaming} onSaveDocument={!isUser ? onSaveDocument : undefined} />
          </div>
        )}

        {/* Tool Calls */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            <button
              onClick={() => setShowTools(!showTools)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground min-h-[44px] py-2"
            >
              <ChevronDown
                className={cn(
                  'h-3 w-3 transition-transform',
                  showTools && 'rotate-180'
                )}
              />
              {message.toolCalls.length} analysis step{message.toolCalls.length !== 1 && 's'}
            </button>

            {showTools && (
              <div className="mt-2 space-y-2">
                {message.toolCalls.map((tool) => (
                  <ToolCallDisplay key={tool.id} toolCall={tool} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sources - Enhanced Citations */}
        {message.sources && message.sources.length > 0 && (
          <CitationCard sources={message.sources} />
        )}

        {/* Action bar — assistant messages: copy + pin */}
        {!isUser && message.content && !message.isStreaming && (
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-slate-100">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
              title={copied ? 'Copied!' : 'Copy message'}
            >
              {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            {onPin && (
              <button
                onClick={() => { onPin(message.id); trackEvent('sage_message_pinned'); }}
                className={cn(
                  'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
                  isPinned ? 'text-amber-600 bg-amber-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                )}
                title={isPinned ? 'Unpin' : 'Pin message'}
              >
                <Pin className="h-3 w-3" />
                {isPinned ? 'Pinned' : 'Pin'}
              </button>
            )}
          </div>
        )}

        {/* Action bar — user messages: edit + copy */}
        {isUser && message.content && !message.isStreaming && !isEditing && (
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-white/20">
            {onEdit && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                title="Edit message"
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
            )}
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              title={copied ? 'Copied!' : 'Copy'}
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}
      </div>

      {isUser && (
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shrink-0 shadow-md ring-2 ring-white">
          <span className="text-xs font-semibold text-white">You</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Message Content (with Markdown-lite rendering)
// ============================================================================

/** Detect document blocks in message content (emails, letters delimited by --- or Subject:) */
function extractDocumentBlocks(content: string): Array<{ type: 'text' | 'document'; content: string; label?: string }> {
  const blocks: Array<{ type: 'text' | 'document'; content: string; label?: string }> = [];

  // Strategy: look for document patterns
  // Pattern 1: Content between --- delimiters that contains Subject: or Dear/Hi [Name]
  // Pattern 2: "Subject:" starts a document block until the next "---" or end

  const lines = content.split('\n');
  let currentText = '';
  let inDoc = false;
  let docContent = '';
  let docLabel = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Strip markdown bold/italic before checking patterns
    const stripped = trimmed.replace(/^\*{1,3}/, '').replace(/\*{1,3}$/, '').trim();

    // Detect document start
    if (!inDoc && (
      stripped.startsWith('Subject:') ||
      stripped.startsWith('RE:') ||
      (stripped.startsWith('Re:') && stripped.includes('Subject')) ||
      (trimmed === '---' && i + 1 < lines.length && (() => {
        const nextStripped = (lines[i + 1]?.trim() || '').replace(/^\*{1,3}/, '').replace(/\*{1,3}$/, '').trim();
        return nextStripped.startsWith('Subject:') ||
          nextStripped.startsWith('Dear ') ||
          nextStripped.startsWith('Hi ') ||
          /^(To|From|Date):/.test(nextStripped);
      })())
    )) {
      // Save preceding text
      if (currentText.trim()) {
        blocks.push({ type: 'text', content: currentText.trimEnd() });
        currentText = '';
      }
      inDoc = true;
      docContent = '';
      if (trimmed === '---') {
        continue; // Skip the delimiter
      }
      if (stripped.startsWith('Subject:')) {
        docLabel = stripped.slice(8).trim();
      }
      docContent += line + '\n';
      continue;
    }

    // Detect document end — explicit delimiter
    if (inDoc && trimmed === '---') {
      blocks.push({ type: 'document', content: docContent.trimEnd(), label: docLabel || 'Document' });
      docContent = '';
      docLabel = '';
      inDoc = false;
      continue;
    }

    // Detect document end — heuristic: commentary section starts after the document sign-off
    // Only trigger on Sage's meta-commentary phrases, NOT on bold headings within the document
    if (inDoc && trimmed !== '' && (
      /^(Why this works|This response|This version|This approach|This email|Here'?s why|Notes?:|Key strategic|Strategic notes?:)/i.test(stripped)
    ) && docContent.includes('\n\n')) {
      // Only split if the doc content contains a sign-off (name at end or --- delimiter)
      const lastLines = docContent.trim().split('\n').slice(-5).join('\n');
      const hasSignoff = /\n(Scott|Janie|Sincerely|Best|Regards|Thank)/i.test(lastLines) || lastLines.includes('---');
      if (hasSignoff) {
        blocks.push({ type: 'document', content: docContent.trimEnd(), label: docLabel || 'Document' });
        docContent = '';
        docLabel = '';
        inDoc = false;
        currentText += line + '\n';
        continue;
      }
    }

    if (inDoc) {
      docContent += line + '\n';
    } else {
      currentText += line + '\n';
    }
  }

  // Flush remaining
  if (inDoc && docContent.trim()) {
    blocks.push({ type: 'document', content: docContent.trimEnd(), label: docLabel || 'Document' });
  }
  if (!inDoc && currentText.trim()) {
    blocks.push({ type: 'text', content: currentText.trimEnd() });
  }

  // If no document blocks found, return everything as text
  if (!blocks.some(b => b.type === 'document')) {
    return [{ type: 'text', content }];
  }

  return blocks;
}

function DocumentBlock({ content, label, onSave }: { content: string; label: string; onSave?: (content: string, label: string) => Promise<void> }) {
  const [blockCopied, setBlockCopied] = useState(false);
  const [htmlCopied, setHtmlCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleBlockCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = content;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setBlockCopied(true);
    trackEvent('sage_document_block_copied', { label });
    setTimeout(() => setBlockCopied(false), 2000);
  };

  const handleCopyHtml = async () => {
    const success = await copyAsHtml(content);
    if (success) {
      setHtmlCopied(true);
      trackEvent('sage_document_html_copied', { label });
      setTimeout(() => setHtmlCopied(false), 2000);
    }
  };

  const handleDownloadTxt = () => {
    downloadAsTextFile(content, label);
    trackEvent('sage_document_txt_downloaded', { label });
  };

  const handleSave = async () => {
    if (!onSave || saving || saved) return;
    setSaving(true);
    try {
      await onSave(content, label);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="my-3 rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-900/10 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-amber-100/50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-300">
          <FileText className="h-3 w-3" />
          {label}
        </div>
        <div className="flex items-center gap-1">
          {onSave && (
            <button
              onClick={handleSave}
              disabled={saving || saved}
              className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-amber-700 hover:text-amber-900 hover:bg-amber-200/50 dark:text-amber-300 dark:hover:bg-amber-800/30 transition-colors disabled:opacity-50"
            >
              {saved ? <Check className="h-3 w-3 text-green-600" /> : saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bookmark className="h-3 w-3" />}
              {saved ? 'Saved!' : saving ? 'Saving...' : 'Save'}
            </button>
          )}
          <button
            onClick={() => {
              downloadDocumentAsPdf(content, label);
              trackEvent('sage_document_pdf_downloaded', { label });
            }}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-amber-700 hover:text-amber-900 hover:bg-amber-200/50 dark:text-amber-300 dark:hover:bg-amber-800/30 transition-colors"
          >
            <Download className="h-3 w-3" />
            PDF
          </button>
          <button
            onClick={handleBlockCopy}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-amber-700 hover:text-amber-900 hover:bg-amber-200/50 dark:text-amber-300 dark:hover:bg-amber-800/30 transition-colors"
            title="Copy plain text"
          >
            {blockCopied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
            {blockCopied ? 'Copied!' : 'Copy'}
          </button>
          <button
            onClick={handleCopyHtml}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-amber-700 hover:text-amber-900 hover:bg-amber-200/50 dark:text-amber-300 dark:hover:bg-amber-800/30 transition-colors"
            title="Copy as formatted HTML (for email)"
          >
            {htmlCopied ? <Check className="h-3 w-3 text-green-600" /> : <Code className="h-3 w-3" />}
            {htmlCopied ? 'Copied!' : 'Copy HTML'}
          </button>
          <button
            onClick={handleDownloadTxt}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-amber-700 hover:text-amber-900 hover:bg-amber-200/50 dark:text-amber-300 dark:hover:bg-amber-800/30 transition-colors"
            title="Download as .txt file"
          >
            <FileDown className="h-3 w-3" />
            .txt
          </button>
        </div>
      </div>
      <div className="px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed font-mono text-slate-800 dark:text-slate-200">
        {content}
      </div>
    </div>
  );
}

function MessageContent({ content, isStreaming, onSaveDocument }: { content: string; isStreaming?: boolean; onSaveDocument?: (content: string, label: string) => Promise<void> }) {
  if (!content && isStreaming) {
    return <span className="animate-pulse">▋</span>;
  }

  // Check for document blocks (emails, letters)
  const blocks = extractDocumentBlocks(content);
  const hasDocBlocks = blocks.some(b => b.type === 'document');

  if (hasDocBlocks) {
    return (
      <>
        {blocks.map((block, idx) => {
          if (block.type === 'document') {
            return <DocumentBlock key={idx} content={block.content} label={block.label || 'Document'} onSave={onSaveDocument} />;
          }
          return <MessageTextContent key={idx} content={block.content} isStreaming={isStreaming} />;
        })}
      </>
    );
  }

  return <MessageTextContent content={content} isStreaming={isStreaming} />;
}

function MessageTextContent({ content, isStreaming }: { content: string; isStreaming?: boolean }) {
  // Simple markdown rendering
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];

  let inCodeBlock = false;
  let codeContent = '';
  let inTable = false;
  let tableRows: string[][] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code blocks
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={i} className="bg-slate-100 rounded p-2 text-xs overflow-x-auto max-w-full">
            <code>{codeContent}</code>
          </pre>
        );
        codeContent = '';
      }
      inCodeBlock = !inCodeBlock;
      continue;
    }

    if (inCodeBlock) {
      codeContent += line + '\n';
      continue;
    }

    // Tables
    if (line.startsWith('|')) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      const cells = line.split('|').slice(1, -1).map((c) => c.trim());
      if (!line.includes('---')) {
        tableRows.push(cells);
      }
      continue;
    } else if (inTable) {
      elements.push(
        <div key={`tw-${i}`} className="overflow-x-auto max-w-full"><table key={i} className="text-xs w-full border-collapse my-2">
          <tbody>
            {tableRows.map((row, ri) => (
              <tr key={ri} className={ri === 0 ? 'font-semibold bg-slate-50' : ''}>
                {row.map((cell, ci) => (
                  <td key={ci} className="border border-slate-200 px-2 py-1">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table></div>
      );
      inTable = false;
      tableRows = [];
    }

    // Headers
    if (line.startsWith('### ')) {
      elements.push(
        <h4 key={i} className="font-semibold text-sm mt-3 mb-1">
          {line.slice(4)}
        </h4>
      );
      continue;
    }
    if (line.startsWith('## ')) {
      elements.push(
        <h3 key={i} className="font-semibold mt-3 mb-1">
          {line.slice(3)}
        </h3>
      );
      continue;
    }

    // Lists
    if (line.match(/^[-*] /)) {
      elements.push(
        <div key={i} className="flex gap-2 ml-2">
          <span>•</span>
          <span>{renderInlineFormatting(line.slice(2))}</span>
        </div>
      );
      continue;
    }

    // Numbered lists
    if (line.match(/^\d+\. /)) {
      const match = line.match(/^(\d+)\. (.*)$/);
      if (match) {
        elements.push(
          <div key={i} className="flex gap-2 ml-2">
            <span className="text-muted-foreground">{match[1]}.</span>
            <span>{renderInlineFormatting(match[2])}</span>
          </div>
        );
      }
      continue;
    }

    // Empty lines
    if (!line.trim()) {
      elements.push(<div key={i} className="h-2" />);
      continue;
    }

    // Regular paragraphs
    elements.push(
      <p key={i} className="mb-1">
        {renderInlineFormatting(line)}
      </p>
    );
  }

  // Handle unclosed table
  if (inTable && tableRows.length > 0) {
    elements.push(
      <div key="final-table-wrap" className="overflow-x-auto max-w-full"><table key="final-table" className="text-xs w-full border-collapse my-2">
        <tbody>
          {tableRows.map((row, ri) => (
            <tr key={ri} className={ri === 0 ? 'font-semibold bg-slate-50' : ''}>
              {row.map((cell, ci) => (
                <td key={ci} className="border border-slate-200 px-2 py-1">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table></div>
    );
  }

  return <>{elements}{isStreaming && <span className="animate-pulse">▋</span>}</>;
}

function renderInlineFormatting(text: string): React.ReactNode {
  // Bold
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Italic
  text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
  // Code
  text = text.replace(/`(.*?)`/g, '<code class="bg-slate-100 px-1 rounded text-xs">$1</code>');

  return <span dangerouslySetInnerHTML={{ __html: text }} />;
}

// ============================================================================
// Tool Call Display
// ============================================================================

function ToolCallDisplay({ toolCall }: { toolCall: ToolCall }) {
  const statusColors = {
    pending: 'bg-slate-100 text-slate-600',
    running: 'bg-blue-100 text-blue-600',
    completed: 'bg-green-100 text-green-600',
    failed: 'bg-red-100 text-red-600',
  };

  return (
    <div className="text-xs bg-slate-50 rounded p-2">
      <div className="flex items-center justify-between">
        <span className="font-medium">{formatToolName(toolCall.name)}</span>
        <Badge variant="outline" className={cn('text-xs', statusColors[toolCall.status])}>
          {toolCall.status}
          {toolCall.duration && ` (${toolCall.duration}ms)`}
        </Badge>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function formatToolName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default SmartChat;
